import {
  Box,
  Chip,
  alpha,
  Alert,
  Paper,
  Stack,
  Select,
  Button,
  Divider,
  Tooltip,
  MenuItem,
  IconButton,
  InputLabel,
  Typography,
  FormControl,
  LinearProgress,
} from '@mui/material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import { FormattedMessage, useIntl } from 'react-intl';
import { useEffect, useMemo, useRef, useState } from 'react';
import TodayRoundedIcon from '@mui/icons-material/TodayRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import { ActivityBoardPermissionItem, HolidayItem } from 'library/types';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import BeachAccessRoundedIcon from '@mui/icons-material/BeachAccessRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import { CalendarViewMode, getVisibleMonths, buildCalendarEventMapForRange } from './utils';
import { MonthCard, ViewModeSwitcher, DayDetailsPopover, DetailedMonthView, SelectedCalendarDay } from './components';

type EmployeeFilterValue = 'all' | number;
type LegendVariant = 'leave' | 'holiday' | 'weekend' | 'halfDay' | 'today';

type LegendItemProps = {
  label: string;
  variant: LegendVariant;
};

const LegendItem = ({ label, variant }: LegendItemProps) => (
  <Stack direction="row" alignItems="center" spacing={0.65}>
    <Box
      aria-hidden="true"
      sx={{
        display: 'grid',
        placeItems: 'center',
        width: 21,
        height: 21,
        flexShrink: 0,
        borderRadius: 0.8,
        border: '1px solid',
        borderWidth: variant === 'today' ? 2 : 1,
        borderColor: (theme) => {
          if (variant === 'today') return theme.palette.primary.main;
          if (variant === 'leave' || variant === 'holiday' || variant === 'halfDay') {
            return alpha(theme.palette.success.main, 0.42);
          }

          return theme.palette.divider;
        },
        backgroundColor: (theme) => {
          if (variant === 'holiday') return alpha(theme.palette.success.main, 0.21);
          if (variant === 'leave' || variant === 'halfDay') return alpha(theme.palette.success.main, 0.14);
          if (variant === 'weekend') return theme.palette.action.disabledBackground;
          return 'transparent';
        },
        color: 'success.main',
        fontSize: '0.46rem',
        fontWeight: 900,
      }}>
      {variant === 'holiday' && <EventRoundedIcon sx={{ fontSize: 11 }} />}
      {variant === 'leave' && <PeopleAltRoundedIcon sx={{ fontSize: 11 }} />}
      {variant === 'halfDay' && '1/2'}
    </Box>

    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
      {label}
    </Typography>
  </Stack>
);

const HolidayYearlyView = () => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const [referenceDate, setReferenceDate] = useState(() => dayjs());
  const currentYear = referenceDate.year();
  const [year, setYear] = useState(currentYear);
  const [viewMode, setViewMode] = useState<CalendarViewMode>('calendarYear');
  const [refreshKey, setRefreshKey] = useState(0);
  const [permissionItems, setPermissionItems] = useState<ActivityBoardPermissionItem[]>([]);
  const [holidayItemsByYear, setHolidayItemsByYear] = useState<Record<number, HolidayItem[]>>({});
  const holidayCacheRef = useRef<Record<number, HolidayItem[]>>({});
  const lastHolidayRefreshKey = useRef<number | null>(null);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [holidaysLoading, setHolidaysLoading] = useState(false);
  const [permissionsError, setPermissionsError] = useState(false);
  const [holidaysError, setHolidaysError] = useState(false);
  const [employeeFilter, setEmployeeFilter] = useState<EmployeeFilterValue>('all');
  const [selectedDay, setSelectedDay] = useState<SelectedCalendarDay | null>(null);

  const loading = permissionsLoading || holidaysLoading;
  const visibleMonths = useMemo(() => getVisibleMonths(viewMode, year, referenceDate), [referenceDate, viewMode, year]);
  const visibleRange = useMemo(
    () => ({
      start: visibleMonths[0].startOf('month'),
      end: visibleMonths[visibleMonths.length - 1].endOf('month'),
    }),
    [visibleMonths]
  );
  const visibleYears = useMemo(
    () => Array.from(new Set(visibleMonths.map((monthDate) => monthDate.year()))),
    [visibleMonths]
  );
  const visibleYearsKey = visibleYears.join(',');
  const holidays = useMemo(
    () =>
      visibleYears.reduce<HolidayItem[]>(
        (items, visibleYear) => items.concat(holidayItemsByYear[visibleYear] || []),
        []
      ),
    [holidayItemsByYear, visibleYears]
  );
  const periodLabel = useMemo(() => {
    if (viewMode === 'calendarYear') return String(year);

    if (viewMode === 'currentMonth') {
      return intl.formatDate(visibleMonths[0].toDate(), { month: 'long', year: 'numeric' });
    }

    const startLabel = intl.formatDate(visibleMonths[0].toDate(), { month: 'short', year: 'numeric' });
    const endLabel = intl.formatDate(visibleMonths[visibleMonths.length - 1].toDate(), {
      month: 'short',
      year: 'numeric',
    });

    return `${startLabel} – ${endLabel}`;
  }, [intl, viewMode, visibleMonths, year]);
  const refreshData = () => {
    setReferenceDate(dayjs());
    setRefreshKey((value) => value + 1);
  };

  useEffect(() => {
    const now = dayjs();
    const delayUntilTomorrow = Math.max(now.add(1, 'day').startOf('day').diff(now) + 1000, 1000);
    const timeoutId = window.setTimeout(() => setReferenceDate(dayjs()), delayUntilTomorrow);

    return () => window.clearTimeout(timeoutId);
  }, [referenceDate]);

  useEffect(() => {
    let active = true;

    const fetchPermissions = async () => {
      try {
        setPermissionsLoading(true);
        setPermissionsError(false);
        const response = await apiService.getActivityBoardPermissions();

        if (!active) return;

        if (response.isSuccess) {
          setPermissionItems(response.data || []);
          return;
        }

        setPermissionsError(true);
        enqueueSnackbar(
          response.message ||
            intl.formatMessage({
              id: 'yearlyHoliday.leaveLoadError',
              defaultMessage: 'Ekip izinleri yüklenirken bir hata oluştu.',
            }),
          { variant: 'error' }
        );
      } catch (error: any) {
        if (!active) return;

        setPermissionsError(true);
        enqueueSnackbar(
          error?.message ||
            intl.formatMessage({
              id: 'yearlyHoliday.leaveLoadError',
              defaultMessage: 'Ekip izinleri yüklenirken bir hata oluştu.',
            }),
          { variant: 'error' }
        );
      } finally {
        if (active) setPermissionsLoading(false);
      }
    };

    fetchPermissions();

    return () => {
      active = false;
    };
  }, [enqueueSnackbar, intl, refreshKey]);

  useEffect(() => {
    let active = true;
    const requestedYears = visibleYearsKey
      .split(',')
      .filter(Boolean)
      .map((value) => Number(value));
    const forceRefresh = lastHolidayRefreshKey.current !== refreshKey;
    lastHolidayRefreshKey.current = refreshKey;
    const yearsToFetch = forceRefresh
      ? requestedYears
      : requestedYears.filter((visibleYear) => !(visibleYear in holidayCacheRef.current));

    if (yearsToFetch.length === 0) {
      setHolidaysError(false);
      setHolidaysLoading(false);
      return;
    }

    const fetchHolidays = async () => {
      try {
        setHolidaysLoading(true);
        setHolidaysError(false);
        const results = await Promise.all(
          yearsToFetch.map(async (visibleYear) => {
            try {
              return {
                succeeded: true as const,
                visibleYear,
                response: await apiService.getHolidays(visibleYear),
              };
            } catch (error: any) {
              return {
                succeeded: false as const,
                visibleYear,
                error,
              };
            }
          })
        );

        if (!active) return;

        const cacheUpdates: Record<number, HolidayItem[]> = {};
        const errorMessages: string[] = [];

        results.forEach((result) => {
          if (!result.succeeded) {
            errorMessages.push(result.error?.message || '');
            return;
          }

          if (result.response.isSuccess) {
            cacheUpdates[result.visibleYear] = Array.from(
              new Map(
                (result.response.data || []).map((holiday) => [
                  `${holiday.localeDateString}-${holiday.id}-${holiday.title}`,
                  holiday,
                ])
              ).values()
            );
          } else {
            errorMessages.push(result.response.message || '');
          }
        });

        if (Object.keys(cacheUpdates).length > 0) {
          const nextCache = { ...holidayCacheRef.current, ...cacheUpdates };
          holidayCacheRef.current = nextCache;
          setHolidayItemsByYear(nextCache);
        }

        if (errorMessages.length > 0) {
          setHolidaysError(true);
          enqueueSnackbar(
            errorMessages[0] ||
              intl.formatMessage({
                id: 'yearlyHoliday.holidayLoadError',
                defaultMessage: 'Resmi tatiller yüklenirken bir hata oluştu.',
              }),
            { variant: 'error' }
          );
        }
      } catch (error: any) {
        if (!active) return;

        setHolidaysError(true);
        enqueueSnackbar(
          error?.message ||
            intl.formatMessage({
              id: 'yearlyHoliday.holidayLoadError',
              defaultMessage: 'Resmi tatiller yüklenirken bir hata oluştu.',
            }),
          { variant: 'error' }
        );
      } finally {
        if (active) setHolidaysLoading(false);
      }
    };

    fetchHolidays();

    return () => {
      active = false;
    };
  }, [enqueueSnackbar, intl, refreshKey, visibleYearsKey]);

  const employees = useMemo(() => {
    const employeeMap = new Map<number, string>();

    permissionItems.forEach((permission) => {
      if (!employeeMap.has(permission.assignedUserId)) {
        employeeMap.set(permission.assignedUserId, permission.nameSurname);
      }
    });

    return Array.from(employeeMap, ([id, name]) => ({ id, name })).sort((left, right) =>
      left.name.localeCompare(right.name, intl.locale)
    );
  }, [intl.locale, permissionItems]);

  const activeEmployeeFilter =
    employeeFilter === 'all' || employees.some((employee) => employee.id === employeeFilter) ? employeeFilter : 'all';

  useEffect(() => {
    if (employeeFilter !== 'all' && !employees.some((employee) => employee.id === employeeFilter)) {
      setEmployeeFilter('all');
    }
  }, [employeeFilter, employees]);

  const eventsByDay = useMemo(
    () =>
      buildCalendarEventMapForRange(
        permissionItems,
        holidays,
        visibleRange.start,
        visibleRange.end,
        activeEmployeeFilter === 'all' ? null : activeEmployeeFilter
      ),
    [activeEmployeeFilter, holidays, permissionItems, visibleRange]
  );

  const stats = useMemo(() => {
    const visibleEmployees = new Set<number>();
    let leaveDayCount = 0;
    let holidayDayCount = 0;

    eventsByDay.forEach((events) => {
      if (events.permissions.length > 0) leaveDayCount += 1;
      if (events.holidays.length > 0) holidayDayCount += 1;
      events.permissions.forEach((permission) => visibleEmployees.add(permission.assignedUserId));
    });

    return {
      employeeCount: visibleEmployees.size,
      leaveDayCount,
      holidayDayCount,
    };
  }, [eventsByDay]);

  useEffect(() => {
    setSelectedDay(null);
  }, [eventsByDay, refreshKey]);

  return (
    <Box sx={{ width: '100%', minWidth: 0, py: 1 }}>
      <Paper
        variant="outlined"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          mb: 2.5,
          borderRadius: 3,
          backgroundImage: (theme) =>
            `radial-gradient(circle at 92% 0%, ${alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.16 : 0.09)}, transparent 34%)`,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: '0 auto 0 0',
            width: 4,
            backgroundColor: 'success.main',
          },
        }}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          alignItems={{ xs: 'stretch', lg: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ p: { xs: 2, sm: 2.5 }, pb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: 46,
                height: 46,
                flexShrink: 0,
                borderRadius: 2,
                color: 'success.main',
                backgroundColor: (theme) => alpha(theme.palette.success.main, 0.13),
              }}>
              <CalendarMonthRoundedIcon />
            </Box>

            <Box>
              <Typography component="h1" variant="h5" sx={{ fontWeight: 800 }}>
                <FormattedMessage id="yearlyHoliday.title" defaultMessage="İzin ve Tatil Takvimi" />
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                <FormattedMessage
                  id="yearlyHoliday.subtitle"
                  defaultMessage="Ekip izinlerini ve resmi tatilleri seçtiğiniz dönemde tek bakışta görün."
                />
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row', lg: 'column', xl: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center', lg: 'flex-end' }}
            justifyContent="flex-end"
            spacing={1.25}
            sx={{ minWidth: 0 }}>
            <ViewModeSwitcher
              value={viewMode}
              onChange={(nextViewMode) => {
                setReferenceDate(dayjs());
                setViewMode(nextViewMode);
                setSelectedDay(null);
              }}
            />

            <Stack
              direction="row"
              alignItems="center"
              justifyContent={{ xs: 'space-between', sm: 'flex-end' }}
              spacing={1}>
              {viewMode === 'calendarYear' ? (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      backgroundColor: 'background.default',
                    }}>
                    <Tooltip
                      title={intl.formatMessage({ id: 'yearlyHoliday.previousYear', defaultMessage: 'Önceki yıl' })}>
                      <IconButton
                        size="small"
                        onClick={() => setYear((previousYear) => previousYear - 1)}
                        aria-label={intl.formatMessage({
                          id: 'yearlyHoliday.previousYear',
                          defaultMessage: 'Önceki yıl',
                        })}>
                        <ChevronLeftRoundedIcon />
                      </IconButton>
                    </Tooltip>

                    <Typography
                      aria-live="polite"
                      sx={{
                        minWidth: 58,
                        textAlign: 'center',
                        fontSize: '1rem',
                        fontWeight: 800,
                        fontVariantNumeric: 'tabular-nums',
                      }}>
                      {year}
                    </Typography>

                    <Tooltip
                      title={intl.formatMessage({ id: 'yearlyHoliday.nextYear', defaultMessage: 'Sonraki yıl' })}>
                      <IconButton
                        size="small"
                        onClick={() => setYear((previousYear) => previousYear + 1)}
                        aria-label={intl.formatMessage({
                          id: 'yearlyHoliday.nextYear',
                          defaultMessage: 'Sonraki yıl',
                        })}>
                        <ChevronRightRoundedIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Tooltip
                    title={intl.formatMessage({ id: 'yearlyHoliday.currentYear', defaultMessage: 'Bu yıla dön' })}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={year === currentYear}
                        onClick={() => setYear(currentYear)}
                        aria-label={intl.formatMessage({
                          id: 'yearlyHoliday.currentYear',
                          defaultMessage: 'Bu yıla dön',
                        })}
                        sx={{ display: { xs: 'inline-flex', sm: 'none' } }}>
                        <TodayRoundedIcon />
                      </IconButton>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<TodayRoundedIcon />}
                        disabled={year === currentYear}
                        onClick={() => setYear(currentYear)}
                        sx={{ display: { xs: 'none', sm: 'inline-flex' }, minHeight: 34 }}>
                        <FormattedMessage id="yearlyHoliday.currentYearShort" defaultMessage="Bu yıl" />
                      </Button>
                    </span>
                  </Tooltip>
                </>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 36,
                    px: 1.25,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    color: 'text.secondary',
                    backgroundColor: 'background.default',
                  }}>
                  <CalendarMonthRoundedIcon sx={{ mr: 0.75, color: 'success.main', fontSize: 18 }} />
                  <Typography
                    aria-live="polite"
                    variant="body2"
                    sx={{
                      fontWeight: 800,
                      textTransform: 'capitalize',
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                    {periodLabel}
                  </Typography>
                </Box>
              )}

              <Tooltip title={intl.formatMessage({ id: 'yearlyHoliday.refresh', defaultMessage: 'Verileri yenile' })}>
                <span>
                  <IconButton
                    size="small"
                    disabled={loading}
                    onClick={refreshData}
                    aria-label={intl.formatMessage({ id: 'yearlyHoliday.refresh', defaultMessage: 'Verileri yenile' })}>
                    <RefreshRoundedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </Stack>

        <Divider />

        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          alignItems={{ xs: 'stretch', lg: 'center' }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{ p: { xs: 2, sm: 2.5 }, pt: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
              <InputLabel id="yearly-holiday-employee-label">
                <FormattedMessage id="yearlyHoliday.employeeFilter" defaultMessage="Personel" />
              </InputLabel>
              <Select
                labelId="yearly-holiday-employee-label"
                value={activeEmployeeFilter}
                label={intl.formatMessage({ id: 'yearlyHoliday.employeeFilter', defaultMessage: 'Personel' })}
                onChange={(event) => setEmployeeFilter(event.target.value as EmployeeFilterValue)}>
                <MenuItem value="all">
                  <FormattedMessage id="yearlyHoliday.allEmployees" defaultMessage="Tüm ekip" />
                </MenuItem>
                {employees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    {employee.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
              <Chip
                size="small"
                variant="outlined"
                icon={<PeopleAltRoundedIcon />}
                label={intl.formatMessage(
                  { id: 'yearlyHoliday.employeeOnLeaveCount', defaultMessage: '{count} izinli personel' },
                  { count: stats.employeeCount }
                )}
              />
              <Chip
                size="small"
                variant="outlined"
                color="success"
                icon={<BeachAccessRoundedIcon />}
                label={intl.formatMessage(
                  { id: 'yearlyHoliday.daysWithLeaveCount', defaultMessage: 'İzin planlı {count} gün' },
                  { count: stats.leaveDayCount }
                )}
              />
              <Chip
                size="small"
                variant="outlined"
                color="success"
                icon={<EventRoundedIcon />}
                label={intl.formatMessage(
                  { id: 'yearlyHoliday.holidayCount', defaultMessage: '{count} resmi tatil' },
                  { count: stats.holidayDayCount }
                )}
              />
            </Stack>
          </Stack>

          <Stack
            direction="row"
            spacing={1.5}
            sx={{ flexWrap: 'wrap', rowGap: 0.75 }}
            role="group"
            aria-label={intl.formatMessage({ id: 'yearlyHoliday.legendAria', defaultMessage: 'Takvim açıklamaları' })}>
            <LegendItem
              variant="leave"
              label={intl.formatMessage({ id: 'yearlyHoliday.legend.leave', defaultMessage: 'İzinli' })}
            />
            <LegendItem
              variant="holiday"
              label={intl.formatMessage({ id: 'yearlyHoliday.legend.holiday', defaultMessage: 'Resmi tatil' })}
            />
            <LegendItem
              variant="weekend"
              label={intl.formatMessage({ id: 'yearlyHoliday.legend.weekend', defaultMessage: 'Hafta sonu' })}
            />
            <LegendItem
              variant="halfDay"
              label={intl.formatMessage({ id: 'yearlyHoliday.legend.halfDay', defaultMessage: 'Yarım gün' })}
            />
            <LegendItem
              variant="today"
              label={intl.formatMessage({ id: 'yearlyHoliday.legend.today', defaultMessage: 'Bugün' })}
            />
          </Stack>
        </Stack>

        {loading && (
          <LinearProgress
            color="success"
            aria-label={intl.formatMessage({ id: 'yearlyHoliday.loading', defaultMessage: 'Takvim yükleniyor' })}
          />
        )}
      </Paper>

      {(permissionsError || holidaysError) && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={refreshData}>
              <FormattedMessage id="yearlyHoliday.retry" defaultMessage="Yeniden dene" />
            </Button>
          }>
          <Stack spacing={0.25}>
            {permissionsError && (
              <Typography variant="body2">
                <FormattedMessage
                  id="yearlyHoliday.leaveUnavailable"
                  defaultMessage="Ekip izinleri güncel olmayabilir."
                />
              </Typography>
            )}
            {holidaysError && (
              <Typography variant="body2">
                <FormattedMessage
                  id="yearlyHoliday.holidayUnavailable"
                  defaultMessage="Resmi tatil verileri şu anda eksik olabilir."
                />
              </Typography>
            )}
          </Stack>
        </Alert>
      )}

      <Box
        aria-busy={loading}
        sx={{
          transition: 'opacity 160ms ease',
          opacity: loading && eventsByDay.size === 0 ? 0.68 : 1,
        }}>
        {viewMode === 'currentMonth' ? (
          <DetailedMonthView
            monthDate={visibleMonths[0]}
            eventsByDay={eventsByDay}
            onDaySelect={(anchorElement, date, events) => setSelectedDay({ anchorElement, date, events })}
          />
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(3, minmax(0, 1fr))',
                xl: 'repeat(4, minmax(0, 1fr))',
              },
              alignItems: 'start',
              gap: 2,
            }}>
            {visibleMonths.map((monthDate) => (
              <MonthCard
                key={monthDate.format('YYYY-MM')}
                year={monthDate.year()}
                month={monthDate.month()}
                showYear={viewMode === 'rollingYear'}
                eventsByDay={eventsByDay}
                onDaySelect={(anchorElement, date, events) => setSelectedDay({ anchorElement, date, events })}
              />
            ))}
          </Box>
        )}
      </Box>

      <DayDetailsPopover selectedDay={selectedDay} onClose={() => setSelectedDay(null)} />
    </Box>
  );
};

export default HolidayYearlyView;
